import { useState, useEffect } from "react";
import { Mail, Lock, ArrowRight, Loader2, Eye, EyeOff } from "lucide-react";
import { apiPost } from "../lib/api";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const data = await apiPost<{ success: boolean; data?: { token: string }; error?: { message: string } }>("/auth/login", {
        email,
        password,
      });

      if (data.success && data.data) {
        localStorage.setItem("token", data.data.token);
        window.location.href = "/dashboard";
      } else {
        setError(data.error?.message || "Erro ao fazer login");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro de conexão");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-dark-900/95 backdrop-blur-md border-b border-white/10 shadow-lg shadow-black/20"
            : "bg-transparent"
        }`}
      >
        <div className="container flex h-16 items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-accent-500 to-accent-600 text-white font-bold text-lg shadow-lg shadow-accent-500/25">
              Z
            </div>
            <span className="text-xl font-bold text-white">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/" className="nav-link">Home</a>
            <a href="/solicitar-demo" className="btn-primary text-sm">
              Demo
              <ArrowRight className="h-4 w-4" />
            </a>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="section bg-dark-900">
        <div className="container">
          <div className="mx-auto max-w-md">
            {/* Background Glow */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="h-96 w-96 rounded-full bg-accent-500/10 blur-3xl" />
            </div>

            <div className="relative card overflow-hidden">
              {/* Header */}
              <div className="mb-8 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-accent-500 to-accent-600 text-white font-bold text-2xl shadow-lg shadow-accent-500/25">
                  Z
                </div>
                <h1 className="mt-6 text-3xl font-bold text-white">Entrar</h1>
                <p className="mt-2 text-surface-400">
                  Acesse sua conta no ZWeb
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-4 text-sm text-red-400">
                    {error}
                  </div>
                )}

                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-medium text-surface-200">
                    <Mail className="h-4 w-4 text-accent-400" />
                    E-mail
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                    placeholder="seu@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-medium text-surface-200">
                    <Lock className="h-4 w-4 text-accent-400" />
                    Senha
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input pr-12"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-white transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-white/20 bg-white/5 text-accent-500 focus:ring-accent-500/20"
                    />
                    <span className="text-sm text-surface-300">Lembrar de mim</span>
                  </label>
                  <a href="/esqueci-senha" className="text-sm text-accent-400 hover:text-accent-300 transition-colors">
                    Esqueceu a senha?
                  </a>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full py-4 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Entrando...
                    </>
                  ) : (
                    <>
                      Entrar
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
              </form>

              {/* Divider */}
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-dark-700 px-4 text-surface-500">ou</span>
                </div>
              </div>

              {/* Register Link */}
              <div className="text-center">
                <p className="text-surface-400">
                  Não tem uma conta?{" "}
                  <a href="/register" className="font-medium text-accent-400 hover:text-accent-300 transition-colors">
                    Cadastre-se
                  </a>
                </p>
              </div>
            </div>

            {/* Footer Text */}
            <p className="mt-6 text-center text-xs text-surface-500">
              Ao entrar, você concorda com nossos{" "}
              <a href="/termos" className="text-accent-400 hover:text-accent-300">Termos de Uso</a>
              {" "}e{" "}
              <a href="/privacidade" className="text-accent-400 hover:text-accent-300">Política de Privacidade</a>.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
